import { Check, X } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsDesktopViewport } from '../../../../hooks/useIsDesktopViewport';
import birthWheel from '../../../../resources/images/birth-chart/birth-wheel-hero.png';
import butterflyLeft from '../../../../resources/images/birth-chart/butterfly-left.png';
import butterflyRight from '../../../../resources/images/birth-chart/butterfly-right.png';
import { setOverflowForSidebar, setPageHiddenBehindModal } from '../../../../utils/helper';
import Button, { BUTTON_STYLE_TYPE } from '../../../common/Button';
import styles from './styles.module.scss';

const BULLETS = ['placements', 'themes', 'planets', 'saved'];

// Desktop-only decoration on the art panel; positions (% of the panel) follow the
// designer's mockup and live in styles.module.scss under `.butterfly1`..`.butterfly4`.
const BUTTERFLIES = [
    { src: butterflyLeft, className: 'butterfly1' },
    { src: butterflyRight, className: 'butterfly2' },
    { src: butterflyRight, className: 'butterfly3' },
    { src: butterflyLeft, className: 'butterfly4' }
];

/**
 * Birth Chart upsell modal shared by the two entry steps (`intro` and
 * `offer`): confirm with the CTA, reject with the X. Layout follows the
 * branded Zara upsell modals (ZaraModalShell / ZaraUpsellModal): full-bleed
 * screen on mobile, centred card with the art as a right-hand panel on
 * desktop.
 */
const HeroModal = ({ onConfirm, onReject }) => {
    const { t } = useTranslation();
    const isDesktop = useIsDesktopViewport();

    // Same background-scroll-lock split as ZaraModalShell.
    useEffect(() => {
        setPageHiddenBehindModal(!isDesktop);
        setOverflowForSidebar(isDesktop);
        return () => {
            setPageHiddenBehindModal(false);
            setOverflowForSidebar(false);
        };
    }, [isDesktop]);

    // The glow is a sibling behind the image (not a filter on it) so it spreads past
    // the wheel's rim without being clipped to the image's rectangle.
    const wheel = (
        <div className={styles.heroWheelWrap} aria-hidden="true">
            <div className={styles.heroGlow} />
            <img className={styles.heroWheel} src={birthWheel} alt="" />
        </div>
    );

    const closeButton = (
        <button
            type="button"
            className={isDesktop ? styles.heroCloseDesktop : styles.heroClose}
            onClick={onReject}
            aria-label={t('birthChart.flow.close')}
        >
            <X size={isDesktop ? 20 : 24} color="#ffffff" />
        </button>
    );

    const content = (
        <>
            <h2 className={styles.heroTitle}>{t('birthChart.flow.intro.title')}</h2>
            <p className={styles.heroText}>{t('birthChart.flow.intro.body')}</p>
            <ul className={styles.bullets}>
                {BULLETS.map((bullet) => (
                    <li key={bullet} className={styles.bullet}>
                        <Check size={20} color="#ffffff" aria-hidden="true" />
                        <span>{t(`birthChart.flow.intro.bullets.${bullet}`)}</span>
                    </li>
                ))}
            </ul>
            <Button
                styleType={BUTTON_STYLE_TYPE.PURPLE}
                text={t('birthChart.flow.intro.cta')}
                onClick={onConfirm}
                extraClasses={[styles.heroPrimary]}
            />
        </>
    );

    if (isDesktop) {
        return (
            <div className={styles.heroDesktopBackdrop} role="dialog" aria-modal="true" aria-label={t('birthChart.flow.intro.title')}>
                <div className={styles.heroDesktopScrim} aria-hidden="true" />
                <div className={styles.heroDesktopCard}>
                    <div className={styles.heroDesktopLeft}>{content}</div>
                    <div className={styles.heroDesktopRight}>
                        {closeButton}
                        {wheel}
                        {BUTTERFLIES.map(({ src, className }) => (
                            <img
                                key={className}
                                className={`${styles.butterfly} ${styles[className]}`}
                                src={src}
                                alt=""
                                aria-hidden="true"
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.heroScreen} role="dialog" aria-modal="true" aria-label={t('birthChart.flow.intro.title')}>
            <div className={styles.heroBg} aria-hidden="true" />
            {closeButton}
            <div className={styles.heroMobileContent}>
                {wheel}
                {content}
            </div>
        </div>
    );
};

export default HeroModal;
